
import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Users, Edit, Trash2, MapPin, Phone, Video, ChevronLeft, ChevronRight, Eye, X } from 'lucide-react';
import { supabase } from '../integrations/supabase/client';
import { useToast } from '../hooks/use-toast';
import NovaEntrevistaModal from './NovaEntrevistaModal';
import { formatTimeToSaoPaulo, isSameDayInSaoPaulo, nowInSaoPaulo } from '../utils/timezone';

interface EntrevistaRH {
  id: string;
  candidato_id: string;
  vaga_id: string;
  data_entrevista: string;
  entrevistador_nome: string;
  entrevistador_email: string;
  tipo_entrevista: string;
  link_reuniao?: string | null;
  observacoes?: string | null;
  status: string;
  duracao_minutos: number;
  candidato_nome?: string;
  vaga_titulo?: string;
}

const AgendaEntrevistas: React.FC = () => {
  const { toast } = useToast();
  const [entrevistas, setEntrevistas] = useState<EntrevistaRH[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNovaEntrevistaModal, setShowNovaEntrevistaModal] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'day' | 'week' | 'month'>('month');
  const [selectedDateForModal, setSelectedDateForModal] = useState<string>('');
  const [showDayViewModal, setShowDayViewModal] = useState(false);
  const [selectedDayForView, setSelectedDayForView] = useState<Date | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEntrevista, setEditingEntrevista] = useState<EntrevistaRH | null>(null);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      // Carregar entrevistas
      const { data: entrevistasData, error: entrevistasError } = await supabase
        .from('entrevistas_rh')
        .select(`
          *,
          candidato:candidatos_rh(nome),
          vaga:vagas_rh(titulo)
        `)
        .order('data_entrevista', { ascending: true });

      if (entrevistasError) throw entrevistasError;

      // Processar dados das entrevistas
      const entrevistasProcessadas: EntrevistaRH[] = entrevistasData?.map(e => ({
        id: e.id,
        candidato_id: e.candidato_id,
        vaga_id: e.vaga_id,
        data_entrevista: e.data_entrevista,
        entrevistador_nome: e.entrevistador_nome,
        entrevistador_email: e.entrevistador_email,
        tipo_entrevista: e.tipo_entrevista,
        link_reuniao: e.link_reuniao,
        observacoes: e.observacoes,
        status: e.status,
        duracao_minutos: e.duracao_minutos,
        candidato_nome: e.candidato?.nome?.includes('Entrevista Coletiva') 
          ? e.candidato.nome.replace('Entrevista Coletiva - ', '') 
          : e.candidato?.nome,
        vaga_titulo: e.vaga?.titulo
      })) || [];

      setEntrevistas(entrevistasProcessadas);

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'agendada': return 'bg-blue-100 text-blue-800';
      case 'confirmada': return 'bg-green-100 text-green-800';
      case 'cancelada': return 'bg-red-100 text-red-800';
      case 'realizada': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'presencial': return <MapPin className="h-4 w-4" />;
      case 'telefone': return <Phone className="h-4 w-4" />;
      case 'video': return <Video className="h-4 w-4" />;
      default: return <Users className="h-4 w-4" />;
    }
  };

  const isEntrevistaColetiva = (entrevista: EntrevistaRH) => {
    return entrevista.observacoes?.includes('ENTREVISTA COLETIVA:') || false;
  };

  const formatarHorarioLocal = (dataHora: string) => {
    console.log('=== DEBUG EXIBIÇÃO ===');
    console.log('Data/hora UTC do banco:', dataHora);
    
    const horarioSaoPaulo = formatTimeToSaoPaulo(dataHora);
    console.log('Horário em São Paulo:', horarioSaoPaulo);
    
    return horarioSaoPaulo;
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    } else {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    }
    setCurrentDate(newDate);
  };

  const getCalendarDays = () => {
    if (viewMode === 'month') {
      const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const startWeek = new Date(start);
      startWeek.setDate(start.getDate() - start.getDay());
      
      const days = [];
      const current = new Date(startWeek);
      
      for (let i = 0; i < 42; i++) {
        days.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      
      return days;
    } else if (viewMode === 'week') {
      const start = new Date(currentDate);
      start.setDate(currentDate.getDate() - currentDate.getDay());
      
      const days = [];
      for (let i = 0; i < 7; i++) {
        const day = new Date(start);
        day.setDate(start.getDate() + i);
        days.push(day);
      }
      
      return days;
    } else {
      return [new Date(currentDate)];
    }
  };

  const getEntrevistasForDay = (day: Date) => {
    return entrevistas.filter(entrevista => {
      return isSameDayInSaoPaulo(entrevista.data_entrevista, day);
    });
  };

  const formatCalendarDate = () => {
    if (viewMode === 'month') {
      return currentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    } else if (viewMode === 'week') {
      const start = new Date(currentDate);
      start.setDate(currentDate.getDate() - currentDate.getDay());
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return `${start.getDate()}/${start.getMonth() + 1} - ${end.getDate()}/${end.getMonth() + 1}/${end.getFullYear()}`;
    } else {
      return currentDate.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });
    }
  };

  const handleDateClick = (day: Date, action: 'schedule' | 'view') => {
    const today = nowInSaoPaulo();
    today.setHours(0, 0, 0, 0);
    
    if (action === 'schedule' && day >= today) {
      // Formatar a data para o formato datetime-local em São Paulo
      const year = day.getFullYear();
      const month = String(day.getMonth() + 1).padStart(2, '0');
      const dayNum = String(day.getDate()).padStart(2, '0');
      const preSelectedDate = `${year}-${month}-${dayNum}T09:00`;
      
      setSelectedDateForModal(preSelectedDate);
      setShowNovaEntrevistaModal(true);
    } else if (action === 'view') {
      setSelectedDayForView(day);
      setShowDayViewModal(true);
    }
  };

  const handleEditarEntrevista = (entrevista: EntrevistaRH) => {
    setEditingEntrevista(entrevista);
    setShowEditModal(true);
  };

  const handleExcluirEntrevista = async (entrevista: EntrevistaRH) => {
    if (window.confirm(`Tem certeza que deseja excluir a entrevista de ${entrevista.candidato_nome}?`)) {
      try {
        const { error } = await supabase
          .from('entrevistas_rh')
          .delete()
          .eq('id', entrevista.id);

        if (error) throw error;

        toast({
          title: "Sucesso",
          description: "Entrevista excluída com sucesso!"
        });

        // Recarregar dados
        carregarDados();
      } catch (error) {
        console.error('Erro ao excluir entrevista:', error);
        toast({
          title: "Erro",
          description: "Erro ao excluir entrevista",
          variant: "destructive"
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Calendar className="w-8 h-8 text-orange-500 mr-3" />
              Agenda de Entrevistas
            </h1>
            <p className="text-gray-600">Gerencie entrevistas e agendamentos</p>
          </div>
        </div>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando agenda...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Calendar className="w-8 h-8 text-orange-500 mr-3" />
            Agenda de Entrevistas
          </h1>
          <p className="text-gray-600">Gerencie entrevistas e agendamentos</p>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={() => setShowNovaEntrevistaModal(true)}
            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova Entrevista
          </button>
        </div>
      </div>

      {/* Controles do Calendário */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold text-gray-900">Calendário de Entrevistas</h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigateDate('prev')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-lg font-medium text-gray-900 capitalize">
                {formatCalendarDate()}
              </span>
              <button
                onClick={() => navigateDate('next')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('day')}
              className={`px-3 py-1 text-sm rounded-md ${
                viewMode === 'day' 
                  ? 'bg-orange-100 text-orange-800' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Dia
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`px-3 py-1 text-sm rounded-md ${
                viewMode === 'week' 
                  ? 'bg-orange-100 text-orange-800' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Semana
            </button>
            <button
              onClick={() => setViewMode('month')}
              className={`px-3 py-1 text-sm rounded-md ${
                viewMode === 'month' 
                  ? 'bg-orange-100 text-orange-800' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Mês
            </button>
          </div>
        </div>

        {/* Vista de Calendário */}
        {viewMode === 'month' && (
          <div className="grid grid-cols-7 gap-1">
            {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
              <div key={day} className="p-2 text-center text-sm font-medium text-gray-500 border-b">
                {day}
              </div>
            ))}
            {getCalendarDays().map((day, index) => {
              const dayEntrevistas = getEntrevistasForDay(day);
              const isCurrentMonth = day.getMonth() === currentDate.getMonth();
              const isToday = day.toDateString() === nowInSaoPaulo().toDateString();
              
              return (
                <div 
                  key={index} 
                  className={`min-h-24 p-1 border border-gray-200 transition-colors relative group ${
                    isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                  } ${isToday ? 'bg-blue-50 border-blue-200' : ''}`}
                >
                  {/* Ícones no canto superior direito no hover */}
                  <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDateClick(day, 'view');
                      }}
                      className="p-1 bg-gray-600 text-white rounded hover:bg-gray-700"
                      title="Visualizar dia"
                    >
                      <Eye className="h-3 w-3" />
                    </button>
                    {isCurrentMonth && day >= nowInSaoPaulo() && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDateClick(day, 'schedule');
                        }}
                        className="p-1 bg-orange-500 text-white rounded hover:bg-orange-600"
                        title="Agendar entrevista"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  <div className={`text-sm ${
                    isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                  } ${isToday ? 'font-bold text-blue-600' : ''} mb-1`}>
                    {day.getDate()}
                  </div>
                  <div className="space-y-1">
                    {dayEntrevistas.slice(0, 2).map(entrevista => {
                      const isColetiva = isEntrevistaColetiva(entrevista);
                      return (
                        <div 
                          key={entrevista.id} 
                          className={`text-xs p-1 rounded text-white truncate ${
                            isColetiva ? 'bg-orange-500' :
                            entrevista.status === 'agendada' ? 'bg-blue-500' :
                            entrevista.status === 'confirmada' ? 'bg-green-500' :
                            entrevista.status === 'cancelada' ? 'bg-red-500' :
                            'bg-purple-500'
                          }`}
                          title={`${isColetiva ? '👥 ' : ''}${entrevista.candidato_nome} - ${entrevista.vaga_titulo}`}
                        >
                          {isColetiva && <Users className="h-3 w-3 inline mr-1" />}
                          {formatarHorarioLocal(entrevista.data_entrevista)} {entrevista.candidato_nome}
                        </div>
                      );
                    })}
                    {dayEntrevistas.length > 2 && (
                      <div className="text-xs text-gray-500">
                        +{dayEntrevistas.length - 2} mais
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {viewMode === 'week' && (
          <div className="grid grid-cols-7 gap-1">
            {['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'].map(day => (
              <div key={day} className="p-2 text-center text-sm font-medium text-gray-500 border-b">
                {day}
              </div>
            ))}
            {getCalendarDays().map((day, index) => {
              const dayEntrevistas = getEntrevistasForDay(day);
              const isToday = day.toDateString() === nowInSaoPaulo().toDateString();
              
              return (
                <div 
                  key={index} 
                  className={`min-h-40 p-2 border border-gray-200 bg-white transition-colors relative group ${
                    isToday ? 'bg-blue-50 border-blue-200' : ''
                  }`}
                >
                  {/* Ícones no canto superior direito no hover */}
                  <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDateClick(day, 'view');
                      }}
                      className="p-1 bg-gray-600 text-white rounded hover:bg-gray-700"
                      title="Visualizar dia"
                    >
                      <Eye className="h-3 w-3" />
                    </button>
                    {day >= nowInSaoPaulo() && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDateClick(day, 'schedule');
                        }}
                        className="p-1 bg-orange-500 text-white rounded hover:bg-orange-600"
                        title="Agendar entrevista"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  <div className={`text-lg font-medium mb-2 ${
                    isToday ? 'text-blue-600' : 'text-gray-900'
                  }`}>
                    {day.getDate()}
                  </div>
                   <div className="space-y-1">
                    {dayEntrevistas.map(entrevista => {
                      const isColetiva = isEntrevistaColetiva(entrevista);
                      return (
                        <div 
                          key={entrevista.id} 
                          className={`text-xs p-2 rounded text-white ${
                            isColetiva ? 'bg-orange-500' :
                            entrevista.status === 'agendada' ? 'bg-blue-500' :
                            entrevista.status === 'confirmada' ? 'bg-green-500' :
                            entrevista.status === 'cancelada' ? 'bg-red-500' :
                            'bg-purple-500'
                          }`}
                        >
                          <div className="font-medium flex items-center">
                            {isColetiva && <Users className="h-3 w-3 mr-1" />}
                            {entrevista.candidato_nome}
                          </div>
                          <div>{formatarHorarioLocal(entrevista.data_entrevista)}</div>
                          <div className="truncate">{entrevista.vaga_titulo}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {viewMode === 'day' && (
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900">
                {currentDate.toLocaleDateString('pt-BR', { 
                  weekday: 'long', 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </h3>
            </div>
            
            <div className="space-y-2">
              {getEntrevistasForDay(currentDate).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nenhuma entrevista agendada para este dia
                </div>
              ) : (
                getEntrevistasForDay(currentDate).map(entrevista => (
                  <div key={entrevista.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="font-medium text-gray-900">
                            {entrevista.candidato_nome}
                          </h3>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(entrevista.status)}`}>
                            {entrevista.status}
                          </span>
                          <div className="flex items-center text-gray-500">
                            {getTipoIcon(entrevista.tipo_entrevista)}
                            <span className="ml-1 text-sm">{entrevista.tipo_entrevista}</span>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                          <div>
                            <span className="font-medium">Vaga:</span> {entrevista.vaga_titulo}
                          </div>
                          <div>
                            <span className="font-medium">Horário:</span> {formatarHorarioLocal(entrevista.data_entrevista)}
                          </div>
                        </div>
                        
                        {entrevista.observacoes && (
                          <div className="mt-2 text-sm text-gray-600">
                            <span className="font-medium">Observações:</span> {entrevista.observacoes}
                          </div>
                        )}
                      </div>
                      
                       <div className="flex space-x-2">
                         <button 
                           onClick={() => handleEditarEntrevista(entrevista)}
                           className="text-blue-600 hover:text-blue-800 transition-colors"
                         >
                           <Edit className="h-4 w-4" />
                         </button>
                         <button 
                           onClick={() => handleExcluirEntrevista(entrevista)}
                           className="text-red-600 hover:text-red-800 transition-colors"
                         >
                           <Trash2 className="h-4 w-4" />
                         </button>
                       </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal Nova Entrevista */}
      <NovaEntrevistaModal
        isOpen={showNovaEntrevistaModal}
        onClose={() => {
          setShowNovaEntrevistaModal(false);
          setSelectedDateForModal(''); // Limpar data selecionada
        }}
        onSuccess={carregarDados}
        preSelectedDate={selectedDateForModal}
      />

      {/* Modal de Visualização do Dia */}
      {showDayViewModal && selectedDayForView && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                  <Eye className="w-6 h-6 text-blue-500 mr-2" />
                  Agenda do Dia - {selectedDayForView.toLocaleDateString('pt-BR', { 
                    weekday: 'long', 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </h2>
                <button
                  onClick={() => {
                    setShowDayViewModal(false);
                    setSelectedDayForView(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-2">
                {getEntrevistasForDay(selectedDayForView).length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      Nenhuma entrevista agendada
                    </h3>
                    <p className="text-gray-600">
                      Não há entrevistas agendadas para este dia
                    </p>
                  </div>
                ) : (
                  getEntrevistasForDay(selectedDayForView).map(entrevista => {
                    const isColetiva = isEntrevistaColetiva(entrevista);
                    return (
                      <div key={entrevista.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="font-medium text-gray-900 flex items-center">
                                {isColetiva && <Users className="h-4 w-4 mr-2 text-orange-500" />}
                                {entrevista.candidato_nome}
                              </h3>
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(entrevista.status)}`}>
                                {entrevista.status}
                              </span>
                              <div className="flex items-center text-gray-500">
                                {getTipoIcon(entrevista.tipo_entrevista)}
                                <span className="ml-1 text-sm">{entrevista.tipo_entrevista}</span>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                              <div>
                                <span className="font-medium">Vaga:</span> {entrevista.vaga_titulo}
                              </div>
                              <div>
                                <span className="font-medium">Horário:</span> {formatarHorarioLocal(entrevista.data_entrevista)}
                              </div>
                              <div>
                                <span className="font-medium">Duração:</span> {entrevista.duracao_minutos} minutos
                              </div>
                              <div>
                                <span className="font-medium">Entrevistador:</span> {entrevista.entrevistador_nome}
                              </div>
                            </div>
                            
                            {entrevista.observacoes && (
                              <div className="mt-2 text-sm text-gray-600">
                                <span className="font-medium">Observações:</span> {entrevista.observacoes}
                              </div>
                            )}

                            {entrevista.link_reuniao && (
                              <div className="mt-2 text-sm text-gray-600">
                                <span className="font-medium">Link da reunião:</span> 
                                <a 
                                  href={entrevista.link_reuniao} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="ml-1 text-blue-600 hover:text-blue-800 underline"
                                >
                                  Abrir link
                                </a>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => handleEditarEntrevista(entrevista)}
                              className="text-blue-600 hover:text-blue-800 transition-colors"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button 
                              onClick={() => handleExcluirEntrevista(entrevista)}
                              className="text-red-600 hover:text-red-800 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="flex justify-center pt-6 border-t mt-6">
                <button
                  onClick={() => {
                    setShowDayViewModal(false);
                    setSelectedDayForView(null);
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
        )}
      
      {/* Modal de Edição de Entrevista */}
      {showEditModal && editingEntrevista && (
        <NovaEntrevistaModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingEntrevista(null);
          }}
          onSuccess={() => {
            carregarDados();
            setShowEditModal(false);
            setEditingEntrevista(null);
          }}
          editingEntrevista={editingEntrevista}
        />
      )}
    </div>
  );
};

export default AgendaEntrevistas;
